import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { applyActionCode, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '@/services/firebase';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';

export default function AuthActionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your request...');
  const [newPassword, setNewPassword] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!mode || !oobCode || !auth) {
      navigate('/app', { replace: true });
      return;
    }

    const handleAction = async () => {
      try {
        if (mode === 'verifyEmail') {
          await applyActionCode(auth, oobCode);
          setStatus('success');
          setMessage('Your email address has been successfully verified.');
        } else if (mode === 'resetPassword') {
          const userEmail = await verifyPasswordResetCode(auth, oobCode);
          setEmail(userEmail);
          setStatus('loading');
          setMessage(''); // Clear message to show password reset form
        } else {
          setStatus('error');
          setMessage('Invalid request mode.');
        }
      } catch (error: any) {
        setStatus('error');
        setMessage(error.message || 'An error occurred while processing your request. The link may have expired.');
      }
    };

    handleAction();
  }, [mode, oobCode]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oobCode || !auth) return;
    
    setStatus('loading');
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStatus('success');
      setMessage('Your password has been successfully reset.');
    } catch (error: any) {
      setStatus('error');
      setMessage(error.message || 'Failed to reset password.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 border border-slate-100 dark:border-slate-700">
        
        {status === 'loading' && !email && (
          <div className="text-center py-8">
            <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Processing</h2>
            <p className="text-slate-500 dark:text-slate-400">{message}</p>
          </div>
        )}

        {status === 'loading' && mode === 'resetPassword' && email && (
          <div className="py-2">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Reset Password</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">Create a new password for {email}</p>
            
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">New Password</label>
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  autoFocus
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter new password"
                />
              </div>
              <Button type="submit" variant="primary" className="w-full" disabled={!newPassword || newPassword.length < 6}>
                Reset Password
              </Button>
            </form>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Success!</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-8">{message}</p>
            <Button variant="primary" className="w-full" onClick={() => navigate('/app')}>
              Continue to App <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Action Failed</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-8">{message}</p>
            <Button variant="outline" className="w-full" onClick={() => navigate('/')}>
              Return Home
            </Button>
          </div>
        )}

      </div>
    </div>
  );
}
